import { ConnectionStatusRow } from '@/app/ConnectionStatusRow';
import { useStrings } from '@/i18n/Localization';
import { useConnectionStatus } from '@/transport/ConnectionStatusProvider';
import styles from '@/app/ConnectionBanner.module.css';

/**
 * Reports connection and storage state to the reader.
 *
 * CR-01 §4 replaces the previous three-second banner and its Offline / Syncing
 * vocabulary with a persistent fixed-height row and four ordinary statuses. The
 * row is always rendered — including Connected — so a transition never moves the
 * message list or the composer, which the appearing-and-vanishing banner did.
 *
 * The storage notice stays beside it because it answers the same question the
 * status does: can what I just typed survive? §4 is explicit that a storage
 * failure must be shown and must not be reported as a queued message.
 */
export function ConnectionBanner() {
  const { t } = useStrings();
  const { status, pendingCount, slow, refreshingChannels, retryNow, storage } =
    useConnectionStatus();

  const showStorage = storage.capability !== 'full';

  return (
    <div className={styles.region}>
      <ConnectionStatusRow
        status={status}
        pendingCount={pendingCount}
        slow={slow}
        refreshingChannels={refreshingChannels}
        onRetryNow={retryNow}
      />
      {showStorage ? (
        <div className={styles.warning} role="status" aria-live="polite">
          {t(storage.capability === 'unavailable' ? 'storage.unavailable' : 'storage.reduced')}
        </div>
      ) : null}
    </div>
  );
}
